/* eslint-disable no-param-reassign */
/**
 * 基于云信上传SDK改编
 * https://github.com/netease-im/VCloud_upload_JS_SDK
 *
 * 与UI完全解绑。
 *
 * 提供 addFile，直接添加视频文件
 *
 */

import $ from 'jquery';
import MD5 from 'crypto-js/md5';
import { EventEmitter } from 'events';
import Cache from '@panda/cache/src/shared/cache';
import CommonCache from '@panda/cache/src/common/cache';
import { FILE_TYPE } from '@panda/cache/src/shared/define/Share';

import {
  UploadConfig,
  AllConfig,
  FileObj,
  UploadStatus,
  UploadEvent,
  ErrorShowType,
  NosInfo,
  TrunkRequest,
  OffsetRequest,
  FileTrunkObj,
} from '../defines/yxUpload';

const g = window;
const cache = new Cache();
const commonCache = new CommonCache();
class YXUploader extends EventEmitter {
  static instance: YXUploader;

  defaults = {
    /**
     * 分片大小
     * @attribute trunkSize
     * @writeOnce
     * @type {Number}
     */
    trunkSize: 4 * 1024 * 1024,
    /**
     * 获取dns列表的URL
     * @attribute urlDns
     * @writeOnce
     * @type {String}
     */
    urlDns: '//wanproxy.127.net/lbs',

    fileExts: [
      'JPG',
      'PNG',
      'WMV',
      'ASF',
      'AVI',
      '3GP',
      'MKV',
      'MP4',
      'DVD',
      'OGM',
      'MOV',
      'MPG',
      'MPEG',
      'MPE',
      'FLV',
      'F4V',
      'SWF',
      'M4V',
      'QT',
      'DAT',
      'VOB',
      'RMVB',
      'RM',
      'OGM',
      'M2TS',
      'MTS',
      'TS',
      'TP',
      'WEBM',
      'MP3',
      'AAC',
    ],
  };

  opts!: AllConfig;

  fileList: FileObj[] = [];

  successList: FileObj[] = [];

  dnsList: string[] = [];

  protocol = g.location.protocol.substring(0, g.location.protocol.length - 1) || 'https';

  constructor(options: UploadConfig = {}) {
    super();
    this.opts = { ...this.defaults, ...options };
  }

  /**
   * 获取初始化信息
   * 发送请求到视频云服务端或应用服务器，参数见代码注释；
   * 其中，typeId和presetId需自行获取(接口文档暂未发布，请联系客服)，headers参数为API token校验返回的结果(必填)
   */
  getInitInfo(file: FileObj) {
    return new Promise<NosInfo>((resolve, reject) => {
      const { yxSign } = this.opts;
      if (!yxSign) {
        this.emit(UploadEvent.Error, {
          errCode: ErrorShowType.Console,
          errMsg: '上传凭证为空',
        });
        reject();
        return;
      }
      const context = localStorage.getItem(`${file.fileKey}_context`);
      if (!context) {
        // TODO: 确认是否http
        // TODO: 确认云信 nostoken 计算是否包含content-type
        $.ajax({
          type: 'post',
          url: '//vcloud.163.com/app/vod/upload/init',
          data: JSON.stringify({
            originFileName: file.file.name, // 上传文件的原始名称(包含后缀名)(必填)(规则同Windows文件名规则)
            userFileName: file.file.name, // 用户命名的上传文件名称(规则同Windows文件名规则)
            typeId: null, // 视频所属的类别ID
            presetId: yxSign.presetId, // 视频所需转码模板ID
            callbackUrl: yxSign.callbackUrl, // 转码成功后回调客户端的URL地址
            description: null, // 上传视频的描述信息
            userDefInfo: { id: yxSign.contentId }, // 用户自定义信息，回调会返回此信息
          }),
          // headers参数为API token校验返回的结果，全部均为必须
          headers: {
            AppKey: yxSign.appKey, // 开发者平台分配的appkey
            Nonce: yxSign.nonce, // 随机数（随机数，最大长度128个字符）
            CurTime: yxSign.curTime, // 当前UTC时间戳，从1970年1月1日0点0分0秒开始到现在的秒数
            CheckSum: yxSign.checkSum, // 服务器认证需要,SHA1(AppSecret+Nonce+CurTime),16进制字符小写
          },
          dataType: 'json',
          contentType: 'application/json',
          success: (data, s, xhr) => {
            /*
              data格式：
              "Content-Type": "application/json; charset=utf-8"
              {
                "code": 200,
                "msg": "",
                "ret": {
                  "xNosToken": "xxsfsgdsgetret",
                  "bucket": "origv10000",
                  "object": "qrwr-eete-dsft-vdfg.mp4"
                }
              }
            */
            if (data.code === 200) {
              localStorage.setItem(`${file.fileKey}_bucket`, data.ret.bucket);
              localStorage.setItem(`${file.fileKey}_object`, data.ret.object);
              localStorage.setItem(
                `${file.fileKey}_xNosToken`,
                data.ret.xNosToken,
              );
              resolve({
                bucketName: data.ret.bucket,
                objectName: data.ret.object,
                nosToken: data.ret.xNosToken,
              });
              commonCache.reportLog({
                data: {
                  status: '[上传视频]云信初始化成功',
                  data: {
                    requestId: data.requestId,
                    contentId: yxSign.contentId,
                    presetId: yxSign.presetId,
                    userFileName: file.file.name, // 用户命名的上传文件名称(规则同Windows文件名规则)
                  },
                },
              });
            } else {
              this.emit(UploadEvent.Error, {
                errCode: ErrorShowType.Console,
                errMsg: data.msg,
              });
              commonCache.reportLog({
                data: {
                  status: '[上传视频]云信初始化失败',
                  data: {
                    ...data,
                    contentId: yxSign.contentId,
                  },
                },
              });
            }
          },
          error: (xhr, s, err) => {
            this.emit(UploadEvent.Error, {
              errCode: ErrorShowType.Console,
              errMsg: err,
            });
            commonCache.reportLog({
              data: {
                status: '[上传视频]云信初始化失败',
                data: {
                  err,
                  contentId: yxSign.contentId,
                },
              },
            });
          },
        });
      } else {
        resolve({
          bucketName: localStorage.getItem(`${file.fileKey}_bucket`) as string,
          objectName: localStorage.getItem(`${file.fileKey}_object`) as string,
          nosToken: localStorage.getItem(`${file.fileKey}_xNosToken`) as string,
        });
        commonCache.reportLog({
          data: {
            status: '[上传视频]云信续传',
            data: {
              contentId: yxSign.contentId,
            },
          },
        });
      }
    });
  }

  /**
   * 添加文件
   * 1. 校验文件格式符合格式要求
   * 2. 添加至待上传列表
   * @param file 待上传文件
   *
   */
  addFile(file: File, fileType?: FILE_TYPE): Promise<FileObj> {
    return cache.getYXSign({
      params: {
        fileName: file.name,
        type: fileType || FILE_TYPE.DEFAULT,
      },
    }).then((yxSign) => {
      // kada log
      commonCache.reportLog({
        data: {
          status: '[上传视频]kada初始化',
          data: yxSign,
        },
      });
      this.opts.yxSign = yxSign;

      const fileExt = file.name.split('.').pop() || '';
      if (!this.opts.fileExts?.includes(fileExt.toUpperCase())) {
        this.emit(UploadEvent.Error, {
          errCode: ErrorShowType.Notify,
          errMsg: '文件类型不匹配，请选择正确格式文件上传',
        });
        throw new Error('文件类型不匹配');
      }
      const fileKey = MD5(`${file.name}:${file.size}:${+new Date()}`).toString();

      const fileObj = {
        fileKey,
        file,
        fileName: file.name,
        fileSizeMb: (file.size / 1024 / 1024).toFixed(2),
        format: file.name.split('.').pop(),
        status: 0,
        checked: true,
        progress: localStorage.getItem(`${fileKey}_progress`) || '0',
        contentId: yxSign.contentId,
      };

      this.fileList.push(fileObj);
      localStorage.setItem(`${fileKey}_created`, `${+new Date()}`);
      return fileObj;
    });
  }

  /**
   * 删除文件
   */
  removeFile(deleteFile?: FileObj) {
    // 不传参情况，删除所有文件
    if (!deleteFile) {
      this.fileList.forEach((file) => {
        this.doRemoveFile(file);
      });
      this.fileList = [];
      return;
    }
    const deleteIndex = this.fileList.findIndex((file) => file.fileKey === deleteFile.fileKey);

    if (deleteIndex === -1) {
      return;
    }
    const deleteFileInList = this.fileList[deleteIndex];
    this.doRemoveFile(deleteFileInList);
    this.fileList.splice(deleteIndex, 1);

    // 如果待删除文件处于上传状态，继续上传下一份文件
    if (deleteFileInList.status === UploadStatus.Uploading) {
      this.upload(deleteIndex);
    }
  }

  doRemoveFile(file: FileObj) {
    if (file.xhr) {
      file.xhr.upload.onprogress = $.noop;
      file.xhr.onreadystatechange = $.noop;
      file.xhr.abort();
      file.xhr = null;
    }
    this.emit(UploadEvent.Remove, file);
  }

  /**
   * 判断文件是否已存在列表中
   */
  checkExist(file: File) {
    const curKey = MD5(`${file.name}:${file.size}`).toString();

    return this.fileList.some((item) => item.fileKey === curKey);
  }

  /**
   * 判断是否有待上传（已选中且上传未完成）的文件
   */
  checkPending() {
    return this.fileList.some(
      (item) => item.checked && item.status === UploadStatus.ToUpload,
    );
  }

  getDNS(bucketName: string) {
    return new Promise<string[]>((resolve) => {
      if (this.dnsList && this.dnsList.length) {
        resolve(this.dnsList);
      } else if (this.protocol === 'https') {
        this.dnsList = ['https://wanproxy-web.127.net'];
        resolve(this.dnsList);
      } else {
        $.ajax({
          type: 'get',
          url: this.opts.urlDns,
          data: {
            version: '1.0',
            bucketname: bucketName,
          },
          dataType: 'json',
          success: (data, s, xhr) => {
            if (data.code) {
              this.emit(UploadEvent.Error, {
                errCode: ErrorShowType.Console,
                errMsg: data.Message,
              });
            } else {
              this.dnsList = data.upload;
              resolve(this.dnsList);
            }
          },
          error: (xhr, s, err) => {
            this.emit(UploadEvent.Error, {
              errCode: ErrorShowType.Console,
              errMsg: err,
            });
          },
        });
      }
    });
  }

  /**
   * 获取上传断点位置
   */
  getOffset(param: OffsetRequest) {
    return new Promise<number>((resolve) => {
      const context = localStorage.getItem(`${param.fileKey}_context`);
      if (!context) {
        resolve(0);
        return;
      }
      $.ajax({
        type: 'get',
        url: `${param.serveIp}/${param.bucketName}/${param.objectName}?uploadContext`,
        data: {
          version: '1.0',
          context,
        },
        dataType: 'json',
        beforeSend(xhr) {
          xhr.setRequestHeader('x-nos-token', param.nosToken);
        },
        success: (data, s, xhr) => {
          if (data.errCode) {
            this.emit(UploadEvent.Error, {
              errCode: ErrorShowType.Console,
              errMsg: data.Message,
            });
          } else {
            resolve(data.offset);
          }
        },
        error: (xhr, s, err) => {
          this.emit(UploadEvent.Error, {
            errCode: ErrorShowType.Console,
            errMsg: err,
          });
        },
      });
    });
  }

  /**
   * 清楚本地缓存
   * @param fileKey
   */
  // eslint-disable-next-line class-methods-use-this
  clearStorage(fileKey: string) {
    localStorage.removeItem(`${fileKey}_progress`);
    localStorage.removeItem(`${fileKey}_context`);
    localStorage.removeItem(`${fileKey}_created`);
    localStorage.removeItem(`${fileKey}_bucket`);
    localStorage.removeItem(`${fileKey}_object`);
    localStorage.removeItem(`${fileKey}_xNosToken`);
  }

  /**
   * 根据fileKey获取指定文件对象
   */
  getFile(fileKey: string) {
    return this.fileList.find((item) => item.fileKey === fileKey);
  }

  /**
   * 上传分片
   */
  uploadTrunk(
    param: TrunkRequest,
    trunkData: FileTrunkObj,
    callback: (data: FileTrunkObj) => void,
  ) {
    const curFile = this.getFile(trunkData.fileKey);
    if (!curFile) {
      this.emit(UploadEvent.Error, {
        errCode: ErrorShowType.Notify,
        errMsg: '文件不存在，请重新上传',
      });
      return;
    }
    const context = localStorage.getItem(`${trunkData.fileKey}_context`);
    let xhrParam = '';
    let xhr: XMLHttpRequest;
    if (curFile.xhr) {
      xhr = curFile.xhr;
    } else {
      xhr = new XMLHttpRequest();
      curFile.xhr = xhr;
    }

    xhr.upload.onprogress = (e) => {
      let progress = 0;

      if (e.lengthComputable) {
        progress = (trunkData.offset + e.loaded) / trunkData.file.size;
        curFile.progress = (progress * 100).toFixed(2);

        if (progress > 0 && progress < 1) {
          curFile.status = UploadStatus.Uploading;
        } else if (progress === UploadStatus.Uploading) {
          curFile.status = UploadStatus.Uploaded;
        }
        localStorage.setItem(`${trunkData.fileKey}_progress`, curFile.progress);
        this.emit(UploadEvent.Progress, {
          progress: curFile.progress,
          fileName: curFile.fileName,
        });
      } else {
        this.emit(UploadEvent.Error, {
          errCode: ErrorShowType.Console,
          errMsg: '浏览器不支持进度事件',
        });
      }
    };

    xhr.onreadystatechange = () => {
      if (xhr.readyState !== 4) {
        return;
      }
      let result;
      try {
        result = JSON.parse(xhr.responseText);
      } catch (e) {
        result = {
          errCode: 500,
          errMsg: '未知错误',
        };
      }
      if (xhr.status === 200) {
        if (!result.errCode) {
          localStorage.setItem(`${trunkData.fileKey}_context`, result.context);

          if (result.offset < trunkData.file.size) {
            // 上传下一片
            this.uploadTrunk(
              param,
              $.extend({}, trunkData, {
                offset: result.offset,
                trunkEnd: result.offset + trunkData.trunkSize,
                context: context || result.context,
              }),
              callback,
            );
          } else {
            // 单文件上传结束
            callback(trunkData);
          }
        } else {
          this.clearStorage(trunkData.fileKey);
          this.emit(UploadEvent.Error, {
            errCode: ErrorShowType.Console,
            errMsg: result.errMsg,
          });
        }
      } else {
        if (xhr.status) {
          // nos error
          this.clearStorage(trunkData.fileKey);
        }
        this.emit(UploadEvent.Error, {
          errCode: ErrorShowType.Console,
          errMsg: xhr.responseText,
        });
      }
    };

    xhrParam = `?offset=${trunkData.offset}&complete=${trunkData.trunkEnd
      >= trunkData.file.size}&context=${context
      || trunkData.context}&version=1.0`;

    xhr.open(
      'post',
      `${param.serveIp}/${param.bucketName}/${param.objectName}${xhrParam}`,
    );
    xhr.setRequestHeader('x-nos-token', param.nosToken);
    xhr.send(trunkData.file.slice(trunkData.offset, trunkData.trunkEnd));
  }

  /**
   * 上传文件操作
   * @param fileIndex 文件索引
   */
  upload(fileIndex = 0) {
    if (!this.checkPending()) {
      this.emit(UploadEvent.Empty);
      return;
    }
    if (fileIndex >= this.fileList.length) {
      this.emit(UploadEvent.AllUploaded);
      return;
    }
    if (
      this.fileList[fileIndex].status === UploadStatus.Uploaded
      || !this.fileList[fileIndex].checked
    ) {
      this.upload(fileIndex + 1);
    }
    const curFile = this.fileList[fileIndex];
    this.getInitInfo(curFile).then((nosInfo) => {
      curFile.objectName = nosInfo.objectName;
      curFile.bucketName = nosInfo.bucketName;
      return this.getDNS(nosInfo.bucketName).then((dnsList) => {
        this.getOffset({
          serveIp: dnsList[0],
          bucketName: nosInfo.bucketName,
          objectName: nosInfo.objectName,
          nosToken: nosInfo.nosToken,
          fileKey: curFile.fileKey,
        }).then((offset) => {
          this.uploadTrunk({
            serveIp: dnsList[0],
            bucketName: nosInfo.bucketName,
            objectName: nosInfo.objectName,
            nosToken: nosInfo.nosToken,
          },
          {
            file: curFile.file,
            fileKey: curFile.fileKey,
            fileIndex,
            offset: offset || 0,
            trunkSize: this.opts.trunkSize,
            trunkEnd: (offset || 0) + this.opts.trunkSize,
            context: '',
          },
          (trunkData) => {
            this.clearStorage(trunkData.fileKey);
            this.onUploaded(curFile);
            this.upload(fileIndex + 1);
          });
        });
      });
    });
  }

  onUploaded(curFile: FileObj) {
    this.successList.push(curFile);
    $.ajax({
      type: 'post',
      url: '//vcloud.163.com/app/vod/video/query',
      data: JSON.stringify({
        objectNames: [curFile.objectName],
      }),
      dataType: 'json',
      contentType: 'application/json',
      // headers参数为API token校验返回的结果，全部均为必须
      headers: {
        AppKey: this.opts.yxSign?.appKey, // 开发者平台分配的appkey
        Nonce: this.opts.yxSign?.nonce, // 随机数（随机数，最大长度128个字符）
        CurTime: this.opts.yxSign?.curTime, // 当前UTC时间戳，从1970年1月1日0点0分0秒开始到现在的秒数
        CheckSum: this.opts.yxSign?.checkSum, // 服务器认证需要,SHA1(AppSecret+Nonce+CurTime),16进制字符小写
      },
      success: (data, s, xhr) => {
        if (data.code === 200) {
          /**
           * 根据需要进行处理，返回的data格式：
           * "Content-Type": "application/json; charset=utf-8"
           * {
           *     "code" : 200,
           *     "msg": "",
           *     "ret" : {
           *         "count": 1,
           *         "list" : [{
           *             "objectName" : "33cf71b1-86ac-4555-a071-d70db07b9685.mp4",
           *             "vid" : 1008
           *         },
           *         ...
           *         ]
           *     }
           * }
           */
          this.emit(UploadEvent.Uploaded, curFile);
          commonCache.reportLog({
            data: {
              status: '[上传视频]完成',
              data: curFile,
            },
          });
        } else {
          this.emit(UploadEvent.Error, {
            errCode: ErrorShowType.Console,
            errMsg: data.msg,
          });
        }
      },
      error: (xhr, s, err) => {
        this.emit(UploadEvent.Error, {
          errCode: ErrorShowType.Console,
          errMsg: err,
        });
      },
    });
  }
}

export default YXUploader;
